import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import {
  Box, Container, Typography, Paper, Button, TextField, IconButton,
  Chip, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, MenuItem, Divider, Tooltip, Alert
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import EditIcon from '@mui/icons-material/Edit';
import LagbasLogo from '../components/LagbasLogo';
import { SECTIONS as STATIC_SECTIONS, PHASES, ROLE_LABELS, ROLE_COLORS } from '../utils/onboardingTemplate';

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

export default function OnboardingTemplateEditor() {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const role = userProfile?.role || '';
  const canEdit = ['vd', 'superadmin', 'ao_ansvarig'].includes(role);

  const [sections, setSections] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [editingSection, setEditingSection] = useState(null); // { idx, title, phase, color, bg }
  const [addItemDialog, setAddItemDialog] = useState(null);   // { sectionIdx }
  const [newItem, setNewItem] = useState({ text: '', role: 'chef', lagbasn: '' });

  useEffect(() => { load(); }, []); // eslint-disable-line

  async function load() {
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, 'settings', 'onboardingTemplate'));
      if (snap.exists() && snap.data()?.sections?.length) {
        setSections(snap.data().sections);
      } else {
        // Deep clone static template
        setSections(JSON.parse(JSON.stringify(STATIC_SECTIONS)));
      }
    } catch {
      setSections(JSON.parse(JSON.stringify(STATIC_SECTIONS)));
    }
    setLoading(false);
  }

  async function handleSave() {
    setSaving(true);
    try {
      await setDoc(doc(db, 'settings', 'onboardingTemplate'), {
        sections,
        totalItems: sections.reduce((sum, s) => sum + s.items.length, 0),
        updatedAt: new Date(),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } finally {
      setSaving(false);
    }
  }

  async function handleReset() {
    if (!window.confirm('Återställ till ursprunglig mall? Alla anpassningar tas bort.')) return;
    setSections(JSON.parse(JSON.stringify(STATIC_SECTIONS)));
  }

  // ── Section operations ──────────────────────────────────────────────
  function moveSection(idx, dir) {
    setSections(prev => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  }

  function deleteSection(idx) {
    if (!window.confirm('Ta bort hela sektionen inkl. alla aktiviteter?')) return;
    setSections(prev => prev.filter((_, i) => i !== idx));
  }

  function addSection() {
    setSections(prev => [...prev, {
      id: uid(),
      phase: 'week1_2',
      title: 'Ny sektion',
      color: '#424242',
      bg: '#f5f5f5',
      items: [],
    }]);
  }

  function saveSectionEdit() {
    if (!editingSection) return;
    setSections(prev => prev.map((s, i) => i === editingSection.idx
      ? { ...s, title: editingSection.title, phase: editingSection.phase }
      : s
    ));
    setEditingSection(null);
  }

  // ── Item operations ──────────────────────────────────────────────────
  function moveItem(sIdx, iIdx, dir) {
    setSections(prev => {
      const next = prev.map(s => ({ ...s, items: [...s.items] }));
      const target = iIdx + dir;
      if (target < 0 || target >= next[sIdx].items.length) return prev;
      [next[sIdx].items[iIdx], next[sIdx].items[target]] = [next[sIdx].items[target], next[sIdx].items[iIdx]];
      return next;
    });
  }

  function deleteItem(sIdx, iIdx) {
    setSections(prev => prev.map((s, i) => i === sIdx
      ? { ...s, items: s.items.filter((_, j) => j !== iIdx) }
      : s
    ));
  }

  function updateItemText(sIdx, iIdx, text) {
    setSections(prev => prev.map((s, i) => i === sIdx
      ? { ...s, items: s.items.map((item, j) => j === iIdx ? { ...item, text } : item) }
      : s
    ));
  }

  function updateItemRole(sIdx, iIdx, role) {
    setSections(prev => prev.map((s, i) => i === sIdx
      ? { ...s, items: s.items.map((item, j) => j === iIdx ? { ...item, role } : item) }
      : s
    ));
  }

  function openAddItem(sectionIdx) {
    setNewItem({ text: '', role: 'chef', lagbasn: '' });
    setAddItemDialog({ sectionIdx });
  }

  function confirmAddItem() {
    if (!newItem.text.trim()) return;
    setSections(prev => prev.map((s, i) => i === addItemDialog.sectionIdx
      ? { ...s, items: [...s.items, { id: uid(), text: newItem.text.trim(), role: newItem.role, ...(newItem.lagbasn ? { lagbasn: newItem.lagbasn } : {}) }] }
      : s
    ));
    setAddItemDialog(null);
  }

  if (!canEdit) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography color="text.secondary">Åtkomst nekad.</Typography>
      </Box>
    );
  }

  const totalItems = sections ? sections.reduce((sum, s) => sum + s.items.length, 0) : 0;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f0f2f5' }}>
      {/* Top bar */}
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LagbasLogo />
          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
          <IconButton color="inherit" size="small" onClick={() => navigate('/onboarding')}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">Redigera onboardingmall</Typography>
            <Typography variant="caption" sx={{ color: '#aaa' }}>
              {totalItems} aktiviteter · ändras för alla kommande onboardingprocesser
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RestoreIcon />} onClick={handleReset}
            size="small"
            sx={{ color: '#aaa', borderColor: 'rgba(255,255,255,0.3)', '&:hover': { borderColor: 'white', color: 'white' } }}>
            Återställ
          </Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave}
            disabled={saving} size="small"
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
            {saving ? 'Sparar…' : 'Spara mall'}
          </Button>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {saved && <Alert severity="success" sx={{ mb: 2 }}>Mall sparad – gäller för alla nya onboardingprocesser.</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>
        ) : (
          <>
            {sections.map((section, sIdx) => {
              const phase = PHASES.find(p => p.key === section.phase);
              return (
                <Paper key={section.id} sx={{ mb: 2, overflow: 'hidden' }}>
                  {/* Section header */}
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    px: 2, py: 1.25,
                    bgcolor: section.bg || '#f5f5f5',
                    borderBottom: '1px solid', borderColor: 'divider',
                  }}>
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ color: section.color }}>
                        {section.title}
                      </Typography>
                      {phase && (
                        <Chip label={phase.label} size="small"
                          sx={{ height: 18, fontSize: '0.62rem', bgcolor: 'rgba(255,255,255,0.6)', color: section.color }} />
                      )}
                      <Chip label={`${section.items.length} aktiviteter`} size="small"
                        sx={{ height: 18, fontSize: '0.62rem', bgcolor: 'rgba(0,0,0,0.07)', color: 'text.secondary' }} />
                    </Box>
                    <Tooltip title="Redigera sektion">
                      <IconButton size="small" onClick={() => setEditingSection({ idx: sIdx, title: section.title, phase: section.phase })}>
                        <EditIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Flytta upp">
                      <span><IconButton size="small" onClick={() => moveSection(sIdx, -1)} disabled={sIdx === 0}>
                        <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                      </IconButton></span>
                    </Tooltip>
                    <Tooltip title="Flytta ned">
                      <span><IconButton size="small" onClick={() => moveSection(sIdx, 1)} disabled={sIdx === sections.length - 1}>
                        <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                      </IconButton></span>
                    </Tooltip>
                    <Tooltip title="Ta bort sektion">
                      <IconButton size="small" onClick={() => deleteSection(sIdx)} sx={{ color: 'error.light' }}>
                        <DeleteIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {/* Items */}
                  {section.items.map((item, iIdx) => {
                    const rc = ROLE_COLORS[item.role] || ROLE_COLORS.any;
                    return (
                      <Box key={item.id} sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        px: 2, py: 0.75,
                        borderBottom: iIdx < section.items.length - 1 ? '1px solid' : 'none',
                        borderColor: 'divider',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
                      }}>
                        <Typography variant="caption" color="text.disabled" sx={{ width: 20, flexShrink: 0, textAlign: 'right' }}>
                          {iIdx + 1}
                        </Typography>
                        <TextField
                          value={item.text}
                          onChange={e => updateItemText(sIdx, iIdx, e.target.value)}
                          variant="standard"
                          size="small"
                          fullWidth
                          InputProps={{ disableUnderline: false, sx: { fontSize: '0.82rem' } }}
                        />
                        <TextField select value={item.role}
                          onChange={e => updateItemRole(sIdx, iIdx, e.target.value)}
                          variant="standard" size="small"
                          sx={{ minWidth: 120, flexShrink: 0 }}
                          SelectProps={{ renderValue: v => (
                            <Chip label={ROLE_LABELS[v] || v} size="small"
                              sx={{ height: 18, fontSize: '0.62rem', bgcolor: rc.bg, color: rc.color, fontWeight: 600 }} />
                          )}}>
                          {Object.entries(ROLE_LABELS).map(([k, l]) => (
                            <MenuItem key={k} value={k}>{l}</MenuItem>
                          ))}
                        </TextField>
                        <Tooltip title="Flytta upp"><span>
                          <IconButton size="small" onClick={() => moveItem(sIdx, iIdx, -1)} disabled={iIdx === 0}>
                            <ArrowUpwardIcon sx={{ fontSize: 13 }} />
                          </IconButton>
                        </span></Tooltip>
                        <Tooltip title="Flytta ned"><span>
                          <IconButton size="small" onClick={() => moveItem(sIdx, iIdx, 1)} disabled={iIdx === section.items.length - 1}>
                            <ArrowDownwardIcon sx={{ fontSize: 13 }} />
                          </IconButton>
                        </span></Tooltip>
                        <Tooltip title="Ta bort aktivitet">
                          <IconButton size="small" onClick={() => deleteItem(sIdx, iIdx)} sx={{ color: 'error.light' }}>
                            <DeleteIcon sx={{ fontSize: 13 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    );
                  })}

                  {/* Add item */}
                  <Box sx={{ px: 2, py: 0.75 }}>
                    <Button size="small" startIcon={<AddIcon />} onClick={() => openAddItem(sIdx)}
                      sx={{ fontSize: '0.72rem', color: section.color, '&:hover': { bgcolor: section.bg } }}>
                      Lägg till aktivitet
                    </Button>
                  </Box>
                </Paper>
              );
            })}

            {/* Add section */}
            <Button variant="outlined" startIcon={<AddIcon />} onClick={addSection}
              sx={{ mt: 1, borderStyle: 'dashed' }}>
              Lägg till sektion
            </Button>
          </>
        )}
      </Container>

      {/* Edit section dialog */}
      <Dialog open={!!editingSection} onClose={() => setEditingSection(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Redigera sektion</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Rubrik" value={editingSection?.title || ''}
              onChange={e => setEditingSection(s => ({ ...s, title: e.target.value }))} fullWidth size="small" />
            <TextField select label="Fas" value={editingSection?.phase || ''}
              onChange={e => setEditingSection(s => ({ ...s, phase: e.target.value }))} fullWidth size="small">
              {PHASES.map(p => <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>)}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingSection(null)}>Avbryt</Button>
          <Button variant="contained" onClick={saveSectionEdit}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>Spara</Button>
        </DialogActions>
      </Dialog>

      {/* Add item dialog */}
      <Dialog open={!!addItemDialog} onClose={() => setAddItemDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Lägg till aktivitet</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Aktivitetsbeskrivning *" value={newItem.text} fullWidth size="small"
              multiline rows={2}
              onChange={e => setNewItem(f => ({ ...f, text: e.target.value }))} />
            <TextField select label="Ansvarig roll" value={newItem.role} fullWidth size="small"
              onChange={e => setNewItem(f => ({ ...f, role: e.target.value }))}>
              {Object.entries(ROLE_LABELS).map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
            </TextField>
            <TextField label="LagbasN-länk (valfri, t.ex. /employees)" value={newItem.lagbasn}
              fullWidth size="small"
              onChange={e => setNewItem(f => ({ ...f, lagbasn: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddItemDialog(null)}>Avbryt</Button>
          <Button variant="contained" onClick={confirmAddItem} disabled={!newItem.text.trim()}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>Lägg till</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
